import { apiCall } from './realApi';

/**
 * Sync funding data from Partner Central
 * @param {string} partnerId - Optional partner ID (for AWS/Aivilo staff)
 * @returns {Promise} Sync response with statistics
 */
export async function syncFundingData(partnerId: string | null = null): Promise<any> {
  const params = new URLSearchParams();
  if (partnerId) {
    params.append('partner_id', partnerId);
  }

  const endpoint = `/funding/sync${params.toString() ? '?' + params : ''}`;

  return apiCall(endpoint, {
    method: 'POST'
  });
}

/**
 * Get benefit applications (funding requests)
 * @param {string} partnerId - Optional partner ID filter
 * @param {string} projectId - Optional project ID filter
 * @returns {Promise<Array>} List of benefit applications
 */
export async function getBenefitApplications(partnerId: string | null = null, projectId: string | null = null): Promise<any> {
  const params = new URLSearchParams();
  if (partnerId) {
    params.append('partner_id', partnerId);
  }
  if (projectId) {
    params.append('project_id', projectId);
  }

  return apiCall(`/funding/applications${params.toString() ? '?' + params : ''}`);
}

/**
 * Get allocations for a specific benefit application
 * @param {string} applicationId - Application ID
 * @returns {Promise<Array>} List of allocations
 */
export async function getAllocations(applicationId: string): Promise<any> {
  return apiCall(`/funding/applications/${applicationId}/allocations`);
}

/**
 * Get credit codes for a specific allocation
 * @param {string} allocationId - Allocation ID
 * @returns {Promise<Array>} List of credit codes
 */
export async function getCreditCodes(allocationId: string): Promise<any> {
  return apiCall(`/funding/allocations/${allocationId}/credit-codes`);
}

interface CreditCodeFilters {
  partnerId?: string;
  clientId?: string;
  status?: string;
}

/**
 * Get all credit codes with optional filters
 * @param {Object} filters - Filter options
 * @param {string} filters.partnerId - Filter by partner
 * @param {string} filters.clientId - Filter by client
 * @param {string} filters.status - Filter by status (Active, Redeemed, Expired, Revoked)
 * @returns {Promise<Array>} List of credit codes
 */
export async function getAllCreditCodes(filters: CreditCodeFilters = {}): Promise<any> {
  const params = new URLSearchParams();

  if (filters.partnerId) {
    params.append('partner_id', filters.partnerId);
  }
  if (filters.clientId) {
    params.append('client_id', filters.clientId);
  }
  if (filters.status) {
    params.append('status_filter', filters.status);
  }

  return apiCall(`/funding/credit-codes${params.toString() ? '?' + params : ''}`);
}

/**
 * Get Partner Central credits summary for a client
 * @param {string} clientId - Optional client ID
 * @returns {Promise} Credits summary with totals and active codes
 */
export async function getCreditsSummary(clientId: string | null = null): Promise<any> {
  const params = new URLSearchParams();
  if (clientId) {
    params.append('client_id', clientId);
  }

  return apiCall(`/funding/credits-summary${params.toString() ? '?' + params : ''}`);
}

/**
 * Associate a funding request with a project
 * @param {string} applicationId - Application ID
 * @param {string} projectId - Project ID (null to disassociate)
 * @returns {Promise} Updated benefit application
 */
export async function updateApplicationProject(applicationId: string, projectId: string | null): Promise<any> {
  const params = new URLSearchParams();
  if (projectId) {
    params.append('project_id', projectId);
  }

  return apiCall(`/funding/applications/${applicationId}/project${params.toString() ? '?' + params : ''}`, {
    method: 'PATCH'
  });
}

/**
 * Get funding requests for a specific project
 * @param {string} projectId - Project ID
 * @returns {Promise<Array>} List of funding requests for the project
 */
export async function getProjectFundingRequests(projectId: string): Promise<any> {
  return apiCall(`/projects/${projectId}/funding-requests`);
}
